<template>
  <EditorUiConfigPanel :expanded="state.expanded" @toggle="togglePanelVisibility">
    <template #header>
      <div>
        <p class="mb-0 mr-2 text-sm font-medium">Slide settings for:</p>
        <p v-if="selectedDataSet" class="mb-0 mr-2 text-sm font-medium">{{ selectedDataSet.name }}</p>
        <p class="text-xs text-gray-400 italic mt-1">Slide settings apply to all commodities</p>
      </div>
    </template>

    <template #button><span /></template>

    <template #default>
      <div class="space-y-4 pt-2 pb-4">
        <div class="flex flex-col space-y-3">
          <template v-if="!activeConfig.clusters">
            <EditorDeckSlidesThreeDDrillDataConfigControl v-if="legends.length">
              <EditorDeckSlidesThreeDDownholeDropdown
                :legends="legends"
                :selected-legend="selectedLegend as Option"
                @select="$emit('select-legend', $event)"
              />
              <p class="ml-2 text-sm">Visible data</p>
            </EditorDeckSlidesThreeDDrillDataConfigControl>

            <div class="flex">
              <EditorDeckSlidesThreeDDrillDataConfigControl>
                <EditorUiConfigButton
                  class="mr-2"
                  :visible="activeConfig.collar?.visible"
                  :is-item-active="allSurfaceMarkersClicked"
                  @toggle-visibility="toggleMarkersMenuVisibility"
                  @toggle-item="toggleSurfaceMarkersMenu"
                />
                <p class="text-sm">All Collar Markers</p>
              </EditorDeckSlidesThreeDDrillDataConfigControl>
              <div v-if="allSurfaceMarkersClicked" ref="markersMenu" class="w-60 absolute -left-64 -mt-2">
                <EditorUiConfigSlider
                  title="All Collar Markers (m)"
                  :default-radius="activeConfig.collar!.radius"
                  @set-radius="handleCollarRadius"
                />
              </div>
            </div>

            <div class="flex">
              <EditorDeckSlidesThreeDDrillDataConfigControl>
                <EditorUiConfigButton
                  class="mr-2"
                  :visible="activeConfig.survey?.visible"
                  :is-item-active="allDrillTracesClicked"
                  @toggle-visibility="toggleTracesMenuVisibility"
                  @toggle-item="toggleDrillTracesMenu"
                />
                <p class="text-sm">All Drill Traces</p>
              </EditorDeckSlidesThreeDDrillDataConfigControl>
              <div v-if="allDrillTracesClicked" ref="tracesMenu" class="w-60 absolute -left-64 -mt-2">
                <EditorUiConfigSlider
                  title="All Drill Traces (m)"
                  :default-radius="activeConfig.survey!.radius"
                  @set-radius="handleDrillTracesRadius"
                />
              </div>
            </div>

            <div class="flex">
              <EditorDeckSlidesThreeDDrillDataConfigControl>
                <EditorUiConfigButton
                  class="mr-2"
                  :visible="activeConfig.highlight?.visible"
                  :is-item-active="allHighlightDataClicked"
                  @toggle-visibility="toggleHighlightMenuVisibility"
                  @toggle-item="toggleHighlightMenu"
                />
                <p class="text-sm">All Downhole Data</p>
              </EditorDeckSlidesThreeDDrillDataConfigControl>
              <div v-if="allHighlightDataClicked" ref="highlightMenu" class="w-60 absolute -left-64 -mt-2">
                <EditorUiConfigSlider
                  title="All Downhole Data (m)"
                  prefix="x"
                  :default-radius="activeConfig.highlight!.radius"
                  @set-radius="handleHighlightRadius"
                />
              </div>
            </div>

            <EditorDeckSlidesThreeDDrillDataConfigControl v-if="legends.length">
              <button
                id="grade-bins-toggle"
                class="py-1.5 px-2.5 w-20 rounded flex justify-center mr-2"
                :class="isGradeBinActive ? 'bg-copper-800' : 'bg-gray-800'"
                @click="$emit('toggle-grade-bins-visibility')"
              >
                <EyeDropperIcon class="w-5 h-5" />
              </button>
              <p class="text-sm">Grade Bins</p>
            </EditorDeckSlidesThreeDDrillDataConfigControl>
          </template>

          <div class="flex">
            <EditorDeckSlidesThreeDDrillDataConfigControl>
              <button
                id="label-size-toggle"
                class="p-2.5 w-20 rounded flex items-center justify-center mr-2"
                :class="allHighlightLabelDataClicked ? 'bg-copper-800' : 'bg-gray-800'"
                @click="toggleSizeMenu"
              >
                <FontType class="w-5" />
              </button>
              <p class="text-sm">Highlight Label Text Size</p>
            </EditorDeckSlidesThreeDDrillDataConfigControl>

            <div v-if="allHighlightLabelDataClicked" ref="labelSizeMenu" class="absolute flex h-10 bg-gray-900 rounded-lg shadow -left-72">
              <EditorLabelSizeMenu v-if="!isAutoScale" :value="size" @input="handleSizeChange" />
              <div v-else class="flex flex-grow min-w-0">
                <EditorFontSizeSelector :value="size" :options="fontOptions" @input="handleSizeChange" />
              </div>

              <button
                class="flex items-center justify-center px-4 text-xs text-white border-l border-transparent gap-2 rounded-r-md box-border"
                :class="isAutoScale ? 'bg-copper-800 hover_bg-copper-900' : 'bg-gray-900 border-gray-800 hover_bg-gray-800'"
                @click="toggleAutoScale"
              >
                <span class="overflow-visible text-sm rounded-none"><FontAwesomeIcon icon="up-right-and-down-left-from-center" /></span>
                Autoscale on zoom
              </button>
            </div>
          </div>
        </div>
      </div>
    </template>
  </EditorUiConfigPanel>
</template>

<script lang="ts" setup>
import { reactive } from 'vue'
import type { PropType, DeepReadonly } from 'vue'
import { EyeDropperIcon } from '@heroicons/vue/20/solid'

import FontType from '@/components/icons/FontType.svg'
import type { Option } from '~/components/VDropdown.vue'
import type { DrillDataConfig } from '~/entities/drillHole'

type State = {
  expanded: boolean
}

const props = defineProps({
  size: {
    type: Number as PropType<number>,
    required: true,
  },
  isGradeBinActive: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  fontOptions: {
    type: Array as PropType<DeepReadonly<{ id: number; name: string; classes: string }[]>>,
    required: true,
  },
  selectedDataSet: {
    type: Object as PropType<{ id: number; name: string } | undefined>,
    required: true,
  },
  isAutoScale: {
    type: Boolean as PropType<boolean>,
    required: true,
  },
  legends: {
    type: Array as PropType<DeepReadonly<Option[]>>,
    required: true,
  },
  selectedLegend: {
    type: Object as PropType<Option>,
    required: true,
  },
  activeConfig: {
    type: Object as PropType<DeepReadonly<DrillDataConfig>>,
    required: true,
  },
})

const emit = defineEmits([
  'sizeChange',
  'toggleAutoScale',
  'select-legend',
  'update-collar-config',
  'update-survey-config',
  'update-highlight-config',
  'toggle-grade-bins-visibility',
])

const state = reactive<State>({
  expanded: true,
})

const {
  elementRef: markersMenu,
  isItemActive: allSurfaceMarkersClicked,
  toggleVisibility: toggleMarkersMenuVisibility,
  toggleItem: toggleSurfaceMarkersMenu,
} = useConfigToggle(() =>
  emit('update-collar-config', {
    ...props.activeConfig,
    collar: { ...props.activeConfig.collar, visible: !props.activeConfig.collar?.visible },
  }),
)
const {
  elementRef: tracesMenu,
  isItemActive: allDrillTracesClicked,
  toggleVisibility: toggleTracesMenuVisibility,
  toggleItem: toggleDrillTracesMenu,
} = useConfigToggle(() =>
  emit('update-survey-config', {
    ...props.activeConfig,
    survey: { ...props.activeConfig.survey, visible: !props.activeConfig.survey?.visible },
  }),
)
const {
  elementRef: highlightMenu,
  isItemActive: allHighlightDataClicked,
  toggleVisibility: toggleHighlightMenuVisibility,
  toggleItem: toggleHighlightMenu,
} = useConfigToggle(() =>
  emit('update-highlight-config', {
    ...props.activeConfig,
    highlight: { ...props.activeConfig.highlight, visible: !props.activeConfig.highlight?.visible },
  }),
)
const {
  elementRef: labelSizeMenu,
  visible: isSizeMenuVisible,
  isItemActive: allHighlightLabelDataClicked,
  toggleVisibility: toggleSizeMenuVisibility,
  toggleItem: toggleSizeMenu,
} = useConfigToggle()

function togglePanelVisibility() {
  state.expanded = !state.expanded
}

function toggleAutoScale() {
  emit('toggleAutoScale')
}

function handleSizeChange(size: number) {
  emit('sizeChange', size)
}

function handleCollarRadius(radius: number) {
  emit('update-collar-config', { ...props.activeConfig, collar: { ...props.activeConfig.collar, radius } })
}

function handleDrillTracesRadius(radius: number) {
  emit('update-survey-config', { ...props.activeConfig, survey: { ...props.activeConfig.survey, radius } })
}

function handleHighlightRadius(radius: number) {
  emit('update-highlight-config', { ...props.activeConfig, highlight: { ...props.activeConfig.highlight, radius } })
}
</script>
